// AllTerra Central — Product Details Tabs (<at-product-tabs>)
// Accessible tablist: click + roving arrow/Home/End keyboard nav, aria-selected,
// and `inert` on hidden panels. Only the tabs with content are rendered server-side.
class AtProductTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));
    if (!this.tabs.length) return;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.onClick);
      tab.addEventListener('keydown', this.onKeydown);
    });

    this.initDragScroll();

    const initial = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
    this.activate(initial);
  }

  // Let a mouse drag the overflowing tab bar horizontally (touch already scrolls
  // natively). A drag past a small threshold suppresses the tab-activation click.
  initDragScroll() {
    const bar = this.querySelector('.at-product-tabs__bar');
    if (!bar) return;
    let down = false;
    let startX = 0;
    let startScroll = 0;
    let moved = 0;

    bar.addEventListener('pointerdown', (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return; // touch = native scroll
      down = true;
      moved = 0;
      startX = e.clientX;
      startScroll = bar.scrollLeft;
      bar.classList.add('at-product-tabs__bar--dragging');
    });

    this.onDragMove = (e) => {
      if (!down) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      bar.scrollLeft = startScroll - dx;
    };
    this.onDragUp = () => {
      if (!down) return;
      down = false;
      bar.classList.remove('at-product-tabs__bar--dragging');
    };
    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragUp);

    // Cancel the click that follows a real drag so it doesn't switch tabs.
    bar.addEventListener(
      'click',
      (e) => {
        if (moved > 5) {
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true
    );
  }

  disconnectedCallback() {
    this.tabs?.forEach((tab) => {
      tab.removeEventListener('click', this.onClick);
      tab.removeEventListener('keydown', this.onKeydown);
    });
    if (this.onDragMove) window.removeEventListener('pointermove', this.onDragMove);
    if (this.onDragUp) window.removeEventListener('pointerup', this.onDragUp);
  }

  onClick(event) {
    this.activate(this.tabs.indexOf(event.currentTarget));
  }

  onKeydown(event) {
    const i = this.tabs.indexOf(event.currentTarget);
    if (i === -1) return;
    const offsets = { ArrowLeft: -1, ArrowRight: 1, Home: -i, End: this.tabs.length - 1 - i };
    const offset = offsets[event.key];
    if (offset === undefined) return;
    event.preventDefault();
    const next = (i + offset + this.tabs.length) % this.tabs.length;
    this.activate(next);
    this.tabs[next]?.focus();
  }

  activate(index) {
    if (index < 0 || index >= this.tabs.length) return;
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', String(active));
      tab.setAttribute('tabindex', active ? '0' : '-1');
    });
    this.panels.forEach((panel, i) => {
      const active = i === index;
      panel.toggleAttribute('inert', !active);
      panel.hidden = !active;
    });
  }
}

if (!customElements.get('at-product-tabs')) {
  customElements.define('at-product-tabs', AtProductTabs);
}
